import React, { FC } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import translate from '@translate';
import Typography from 'src/components/Typography';
import { Divider, Box } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import ListItem from '@mui/material/ListItem';
import LogoutIcon from '@mui/icons-material/Logout';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ProviderType } from 'src/types/provider';
import stringInText from 'src/utils/stringInText';
import TextField from 'src/components/TextField';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import MUIMenu, { MenuProps } from '@mui/material/Menu';
import VritualRender from 'src/components/VritualRender';
import { AvatarIcon } from 'src/components/SkladonIcon/Icons';
import { getProviderStatusColor } from 'src/utils/providerStatusColorMap';

const Menu = styled(MUIMenu)<MenuProps>(({ theme }) => ({
    '& .MuiPaper-root': {
        width: '248px',
        borderRadius: sizes.radius,
        boxShadow: '0px 4px 8px 0px #56495929; 0px 0px 2px 0px #938B950A; 0px 1px 2px 0px #847B880F;',

        '& .MuiList-root': {
            paddingTop: 0,
            paddingBottom: 0,

            ' & .MuiListSubheader-root': {
                height: '48px',
                padding: '13px 16px',
                backgroundColor: colors.background_B2,
            },

            '& .MuiTypography-body2': {
                fontWeight: sizes.bold_W,
            },

            '& .MuiMenuItem-root': {
                height: '52px',

                '&:hover': {
                    backgroundColor: colors.background_B1,
                },

                '& svg': {
                    width: '28px',
                    height: '28px',
                },
            },

            '& .providers-list': {
                '& .providers-list-items .MuiTypography-body1': {
                    whiteSpace: 'normal',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                },
                '& .MuiTextField-root': {
                    paddingTop: '0px',
                    paddingBottom: '0px',
                },
            },
        },
    },
}));

type UserMenuPropsType = {
    anchorEl: Element | null | undefined;
    onClose: MenuProps['onClose'];
    provider: ProviderType;
    providers: ProviderType[];
    handleChangeProvider: (provider: ProviderType) => void;
    handleProfile: () => void;
    handlLogout: () => void;
};

const UserMenu: FC<UserMenuPropsType> = (props) => {
    const [providerFilter, setProviderFilter] = React.useState('');

    const menuId = 'primary-user-menu';
    const isOpen = Boolean(props.anchorEl);

    const renderProviders = (isOpen: boolean) => {
        if (!isOpen) {
            return null;
        }
        const filteredProviders = props.providers.filter((providerItem) => {
            if (
                providerItem.ESHOP_KEY !== props.provider.ESHOP_KEY &&
                providerItem.COMPANY_NAME &&
                stringInText(providerItem.COMPANY_NAME, providerFilter)
            ) {
                return true;
            }
            return false;
        });

        if (props.providers.length < 2) {
            return null;
        }

        return (
            <div className="providers-list">
                <ListItem>
                    <Typography variant="body2">{translate('appBar.switchToAccount')}</Typography>
                </ListItem>
                {props.providers.length > 6 ? (
                    <ListItem>
                        <TextField
                            value={providerFilter}
                            fullWidth
                            type="text"
                            onChange={(value) => setProviderFilter(String(value || ''))}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                            }}
                            autofocus
                        />
                    </ListItem>
                ) : null}
                <div className="providers-list-items">
                    <VritualRender
                        rows={filteredProviders}
                        maxHeight={260}
                        rowId="ESHOP_KEY"
                        renderRow={(providerItem) => (
                            <MenuItem
                                onClick={() => props.handleChangeProvider(providerItem)}
                                title={providerItem.COMPANY_NAME}
                            >
                                <ListItemIcon>
                                    <AvatarIcon />
                                </ListItemIcon>
                                <Typography variant="body1" sx={{ color: getProviderStatusColor(providerItem.STATUS, true) }}>
                                    {providerItem.COMPANY_NAME}
                                </Typography>
                            </MenuItem>
                        )}
                    />
                </div>
            </div>
        );
    };

    return (
        <Menu
            anchorEl={props.anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isOpen}
            onClose={props.onClose}
        >
            <ListSubheader onClick={props.handleProfile} sx={{ cursor: 'pointer' }}>
                <Typography variant="h3">
                    <ListItemIcon sx={{ verticalAlign: 'bottom', minWidth: '36px' }}>
                        <AvatarIcon />
                    </ListItemIcon>
                    {translate('appBar.userSettings')}
                </Typography>
            </ListSubheader>
            <Divider />
            <ListItem>
                <Typography variant="body2">{translate('appBar.currentAccount')}</Typography>
            </ListItem>
            <MenuItem>
                <ListItemIcon>
                    <AvatarIcon />
                </ListItemIcon>
                <Typography variant="body1" sx={{ color: getProviderStatusColor(props.provider.STATUS, true) }}>
                    {props.provider.COMPANY_NAME}
                </Typography>
            </MenuItem>
            {renderProviders(isOpen)}
            <Divider />
            <MenuItem onClick={props.handlLogout}>
                <ListItemIcon>
                    <LogoutIcon />
                </ListItemIcon>
                {translate('appBar.logout')}
            </MenuItem>
        </Menu>
    );
};

export default UserMenu;
